// Project Demos — types mirror demoRequestSchema and /api/demos contracts.

export type DemoStatus =
  | 'requested'
  | 'assigned'
  | 'in_development'
  | 'review'
  | 'approved'
  | 'shared'
  | 'delivered'
  | 'rejected'
  | 'closed'
  | 'pending'
  | 'in_progress';

export type DemoPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface DemoClient {
  name?: string;
  company?: string;
  contact_person?: string;
  mobile?: string;
  email?: string;
}

export interface DemoAttachment {
  file: string;
  name: string;
  type?: string;
  size?: number;
}

export interface Demo {
  id: string;
  demo_code?: string;
  title: string;
  description?: string;
  status: DemoStatus;
  priority?: DemoPriority;
  progress?: number;
  client?: DemoClient;
  lead_id?: string | null;
  project_name?: string;
  project_type?: string;
  requirements?: string;
  expected_features?: string;
  reference_urls?: string[];
  attachments?: DemoAttachment[];
  requested_date?: string;
  expected_delivery_date?: string;
  requester_name?: string;
  requester_dept?: string;
  requested_by?: string | { _id?: string; id?: string; display_name?: string };
  assignee_name?: string;
  assigned_to?: string | { _id?: string; id?: string; display_name?: string } | null;
  dev_hours?: number;
  review_hours?: number;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
}

export interface DemoComment {
  id: string;
  comment: string;
  author_name?: string;
  created_at: string;
}

export interface DemoActivity {
  id: string;
  action: string;
  detail?: string;
  user_name?: string;
  created_at: string;
}

export interface DemoFeedback {
  id: string;
  rating?: number;
  feedback?: string;
  suggestions?: string;
  status?: string;
  revision_required?: boolean;
  author_name?: string;
  created_at: string;
}

export interface DemoDetail {
  demo: Demo;
  comments: DemoComment[];
  activities: DemoActivity[];
  feedback: DemoFeedback[];
}

export interface DemosFilters {
  status?: string;
  priority?: string;
  mine?: boolean;
}

export interface AttachmentUpload {
  data: string;
  name: string;
  type: string;
}

export interface CreateDemoPayload {
  title: string;
  description?: string;
  priority?: DemoPriority;
  project_type?: string;
  client?: DemoClient;
  lead_id?: string;
  requirements?: string;
  expected_features?: string;
  expected_delivery_date?: string;
  attachments?: AttachmentUpload[];
  assigned_to?: string;
}

export interface UpdateDemoPayload {
  status?: DemoStatus;
  assigned_to?: string | null;
  priority?: DemoPriority;
  progress?: number;
  rejection_reason?: string;
  dev_hours?: number;
  review_hours?: number;
  expected_delivery_date?: string;
}

export interface FeedbackPayload {
  rating?: number;
  feedback?: string;
  suggestions?: string;
  status?: 'accepted' | 'revision_required' | 'rejected';
}

export function getAssignedId(a: Demo['assigned_to']): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.id ?? a._id ?? '';
}
